import { collectContext, collectLocale } from "./context";
import { getOrCreateDeviceId, getTrackerClickId } from "./ids";
import { collectClientHints } from "./signals/clientHints";
import { collectHeadless } from "./signals/headless";
import { collectNavigator, collectScreen } from "./signals/navigator";
import { collectWebgl } from "./signals/webgl";
import { collectWebrtc } from "./signals/webrtc";
import { runThumbmark } from "./thumbmark";
import type { CollectOptions, CollectPayload, NetworkSignals } from "./types";

export const SDK_VERSION = "0.1.0";

const DEFAULTS = {
  cookieName: "_as_did",
  trackerClickIdParams: ["tracker_click_id", "click_id", "clickid", "cid", "subid"],
  timeoutMs: 4000,
  webrtc: true,
  webrtcTimeoutMs: 1200,
};

/**
 * Collect the full client-side fingerprint payload. Pure collection: no transport,
 * no encryption. Every probe is individually guarded — collect() resolves even in
 * hostile/locked-down browsers, with missing signals nulled out.
 */
export async function collect(options: CollectOptions = {}): Promise<CollectPayload> {
  const startedAt = now();
  const opts = { ...DEFAULTS, ...options };

  // Synchronous probes first — cheap and never throw past their guards.
  const navigatorSignals = collectNavigator();
  const screenSignals = collectScreen();
  const webgl = collectWebgl();
  const headless = collectHeadless(webgl.webglUnmaskedRenderer);
  const locale = collectLocale();
  const context = collectContext();

  const cookieDeviceId = getOrCreateDeviceId(opts.cookieName);
  // Явный subid с тега (`data-subid`) приоритетнее URL-детекта.
  const trackerClickId = normalizeSubId(opts.trackerClickId) ?? getTrackerClickId(opts.trackerClickIdParams);

  // Async probes in parallel under their own budgets.
  const [thumb, clientHints, network] = await Promise.all([
    runThumbmark(opts.timeoutMs),
    collectClientHints(),
    opts.webrtc
      ? collectWebrtc(opts.webrtcTimeoutMs)
      : Promise.resolve<NetworkSignals>({ webrtcPublicIp: null, webrtcLocalIps: [] }),
  ]);

  return {
    identifiers: {
      // visitorId (click-id) присваивает сервер на /collect — клиент его не генерит.
      cookieDeviceId,
      fingerprintId: thumb.thumbmark ?? cookieDeviceId,
      trackerClickId,
    },
    graphics: {
      canvasHash: thumb.canvasHash,
      audioHash: thumb.audioHash,
      fontsHash: thumb.fontsHash,
      mathHash: thumb.mathHash,
      pluginsHash: thumb.pluginsHash,
      ...webgl,
    },
    navigator: navigatorSignals,
    clientHints,
    screen: screenSignals,
    network,
    headless,
    locale,
    context,
    meta: {
      sdkVersion: SDK_VERSION,
      thumbmarkVersion: thumb.version,
      collectedAt: new Date().toISOString(),
      elapsedMs: Math.round(now() - startedAt),
    },
  };
}

function now(): number {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

/**
 * Нормализация явного subid. Неподставленный трекером макрос (`{subid}`, `{{subid}}`,
 * `[[subid]]`) → `undefined`, чтобы литерал плейсхолдера не попал в клик как id.
 */
function normalizeSubId(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if (!v || /[{}[\]]/.test(v)) return undefined;
  return v;
}
